import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageThreadModel } from '../models/MessageThreadModel';
import { MessageModel } from '../models/MessageModel';
import { formatDate } from '../utils/dateUtils';
import messageService from '../services/messageService';
import { useAppContext } from '../context/AppContext';
import Styles from './MessagesPage.module.css';
import buyerService from '../services/buyerService';
import itemService from '../services/itemService';
import { ItemModel } from '../models/ItemModel';
import { formatPrice } from '../utils/currencyUtils';
import { Link, useParams } from 'react-router-dom';
import { useReferenceDataContext } from '../context/ReferenceDataContext';
import { useWindowSize } from '../hooks/useWindowSize';
import SidebarComp from '../components/SideBarComp';
import { OfferModel } from '../models/OfferModel';
import offerService from '../services/offerService';
import MessageThreadListComp from '../components/MessageThreadListComp';
import { MessageAttachmentModel } from '../models/MessageAttachmentModel';
import MessageBubbleComp from '../components/MessageBubbleComp';
import UploadProgressModalComp from '../components/UploadProgressModalComp';



const MessagesPage = () => {
    const { threadId } = useParams<{ threadId: string }>();
    const { seller } = useAppContext();
    const [sellerId] = useState(seller!.sellerId); //seller id 
    const { categories } = useReferenceDataContext(); //categories for filtering items

    const itemImageURL = process.env.REACT_APP_ITEM_IMAGE_URL;
    const messageAttachmentURL = process.env.REACT_APP_MESSAGE_ATTACHMENT_URL;
    const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatusText, setUploadStatusText] = useState('');

    const [selectedThread, setSelectedThread] = useState<MessageThreadModel>();
    const [selectedThreadBuyerName, setSelectedThreadBuyerName] = useState<string>();
    const [selectedItemInfo, setSelectedItemInfo] = useState<ItemModel>();
    const [selectedOfferInfo, setSelectedOfferInfo] = useState<OfferModel>();
    const [messageThreads, setMessageThreads] = useState<MessageThreadModel[]>([]);
    const [messages, setMessages] = useState<MessageModel[]>([]);
    const [filter, setFilter] = useState<'all' | 'read' | 'unread'>('all');
    const [attachments, setAttachments] = useState<File[]>([]);
    const [attachmentError, setAttachmentError] = useState<string>("");

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [pageNum, setPageNum] = useState(0); //current page number

    const pageSize = 10; //number of items per page
    const [hasMoreThreads, setHasMoreThreads] = useState(false);
    const bottomRef = useRef<HTMLLIElement | null>(null);
    const isLoadingThreadsRef = useRef(false);

    const [message, setMessage] = useState('');
    const [sendEmailCopy, setSendEmailCopy] = useState(true);

    const bottomMessageRef = useRef<HTMLDivElement | null>(null);
    const messageContainerRef = useRef<HTMLDivElement | null>(null);
    const messageInputRef = useRef<HTMLTextAreaElement | null>(null);


    const width = useWindowSize();
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const isMobile = width <= 768;


    const [serviceError, setServiceError] = useState(''); //error for service call

    const [isSending, setIsSending] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false); //indicates if initial load is done
    const [isItemInfoLoaded, setIsItemInfoLoaded] = useState(false);


    const validFileExtensions = [
        ".jpg", ".jpeg", ".png", ".gif",
        ".pdf", ".csv", ".xls", ".xlsx",
        ".txt", ".doc", ".docx"
    ];

    const acceptedFileTypes = validFileExtensions.join(",");

    const getFileExtension = (fileName?: string) => {
        if (!fileName) return '';
        const index = fileName.lastIndexOf('.');
        return index >= 0 ? fileName.substring(index).toLowerCase() : '';
    };



    const handleOpenAttachment = async (
        threadId: number,
        messageId: number,
        attachment: MessageAttachmentModel
    ) => {
        try {
            const blob = await messageService.getMessageAttachment(
                threadId,
                messageId,
                attachment.messageAttachmentId
            );

            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');

            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 60000);
        } catch (error) {
            console.error('Error opening attachment:', error);
            setServiceError('Failed to open attachment. Please try again later.');
        }
    };

    const isValidAttachmentFile = (file: File) => {
        const ext = getFileExtension(file.name);
        return validFileExtensions.includes(ext);
    };
    const getBuyer = async (buyerId: number) => {
        const buyer = await buyerService.getBuyer(buyerId);
        setSelectedThreadBuyerName(buyer.firstname + " " + buyer.lastname);
    };

    const getReadUnreadFilterValue = (filter: string) => {
        if (filter === 'all') return -1;
        if (filter === 'read') return 0;
        if (filter === 'unread') return 1;
    }

    const getItemInfo = async (itemId: number) => {
        setIsItemInfoLoaded(false); // reset before fetching
        try {
            const item = await itemService.getItem(itemId);
            setSelectedItemInfo(item);
        } catch (e) {
            setSelectedItemInfo(undefined);
        } finally {
            setIsItemInfoLoaded(true); // mark fetch as done
        }
    };

    const getOfferInfo = async (offerId: number) => {
        try {
            const offer = await offerService.getOffer(offerId);
            setSelectedOfferInfo(offer);
        }
        catch (e) {
            setSelectedOfferInfo(undefined);
        }
    }

    const handleUploadModalClose = useCallback(() => {
        setIsUploadingAttachments(false);
    }, []);

    const handleAddAttachmentsClick = () => {
        setAttachmentError("");
        fileInputRef.current?.click();
    };

    const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAttachmentError("");

        const picked = Array.from(e.target.files ?? []);
        if (picked.length === 0) {
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const invalidFiles = picked.filter(file => !isValidAttachmentFile(file));

        if (invalidFiles.length > 0) {
            setAttachmentError(
                `Only these file types are allowed: ${validFileExtensions.join(", ")}`
            );

            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const existingKey = new Set(attachments.map(f => `${f.name}|${f.size}`));
        const filtered = picked.filter(f => !existingKey.has(`${f.name}|${f.size}`));

        const totalAfter = attachments.length + filtered.length;
        if (totalAfter > 3) {
            setAttachmentError("You can attach up to 3 files maximum.");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setAttachments(prev => [...prev, ...filtered]);

        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleRemoveAttachment = (index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
        setAttachmentError("");
    };

    const handleFilterChange = (newFilter: 'all' | 'read' | 'unread') => {
        setFilter(newFilter);
        setPageNum(0);
        setMessages([]);
        setSelectedThread(undefined);
        setSelectedThreadBuyerName('');
        setSelectedItemInfo(undefined);
        setMessageThreads([]);
        setHasMoreThreads(true);
    }

    const handleThreadClick = (thread: MessageThreadModel) => {
        if (!thread) return;

        setSelectedThread({ ...thread });
        setMessages([]);
        setServiceError('');
        if (thread.sellerUnread) {
            const markRead = async () => {
                const response = await messageService.markMessageThreadRead(thread.messageThreadId, 0);
                if (response === 'Success') {
                    thread.sellerUnread = false; // Update the thread state locally
                }
            }
            markRead();
        }
        getBuyer(thread.buyerId);

        setSelectedItemInfo(undefined);
        setSelectedOfferInfo(undefined);

        if (thread.itemId) {
            getItemInfo(thread.itemId);
        }
        else if (thread.offerId) {
            getOfferInfo(thread.offerId);
        }

        if (messageInputRef.current) {
            messageInputRef.current.focus();
        }
    }



    const handleSend = async () => {
        if (!message.trim() || !selectedThread) return;

        const currentThreadId = selectedThread.messageThreadId;
        const filesToUpload = [...attachments];
        const messageText = message.trim();

        try {
            setServiceError('');
            setIsSending(true);

            const result = await messageService.sendMessage(
                currentThreadId,
                messageText,
                sendEmailCopy
            );

            if (result.result === 'Success') {
                const newMessage = result.message;

                // add message to UI first so later attachment update can find it
                setMessages(prev => [...prev, newMessage]);

                if (filesToUpload.length > 0) {
                    try {
                        setIsUploadingAttachments(true);
                        setUploadProgress(0);
                        setUploadStatusText(`Uploading ${filesToUpload.length} attachment(s)...`);

                        await messageService.uploadMessageAttachments(
                            currentThreadId,
                            newMessage.messageId,
                            filesToUpload,
                            (progressEvent: any) => {
                                if (!progressEvent.total) return;

                                const percent = Math.round(
                                    (progressEvent.loaded * 100) / progressEvent.total
                                );
                                setUploadProgress(percent);
                            }
                        );

                        await loadAttachmentsForMessage(currentThreadId, newMessage.messageId);
                    } catch (uploadError: any) {
                        console.error("Error uploading attachments:", uploadError);
                        setServiceError(
                            uploadError?.message || 'Message sent, but attachment upload failed.'
                        );
                    } finally {
                        setIsUploadingAttachments(false);
                        setUploadProgress(0);
                        setUploadStatusText('');
                    }
                }

                setMessage('');
                setAttachments([]);
                setAttachmentError('');
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
            else if (result.result === 'TooFrequent') {
                setServiceError('You are sending messages too frequently. Please wait a moment and try again.');
            }
            else if (result.result === 'Error') {
                setServiceError('Failed to send message. Please try again later.');
            }
            else {
                setServiceError('Failed to send message. Due to unknown error, please try again later.');
            }
        } catch (e: any) {
            console.error("Error sending message:", e);
            setServiceError(e?.message || 'Failed to send message. Please try again later.');
        } finally {
            setIsSending(false);
        }
    };


    const loadThreads = useCallback(async (pageNum: number = 0) => {
        if (isLoadingThreadsRef.current) return;

        isLoadingThreadsRef.current = true;

        try {
            const response = await messageService.getMessageThreads(sellerId, getReadUnreadFilterValue(filter), pageNum * pageSize, pageSize);
            if (response.length === 0) {
                setHasMoreThreads(false);
            }
            else if (response.length < pageSize) {
                setHasMoreThreads(false);
                setMessageThreads(prev => [...prev, ...response]);
            }
            else {
                setHasMoreThreads(true);
                setMessageThreads(prev => [...prev, ...response]);
            }

            if (threadId) {
                const thread = response.find(t => t.messageThreadId === parseInt(threadId));
                if (thread) {
                    handleThreadClick(thread);
                }
            }
        }
        catch (error) {
            console.error("Error loading message threads:", error);
            setServiceError('Failed to load message threads. Please try again later.');
        }
        finally {
            isLoadingThreadsRef.current = false;
            setIsLoading(false);
            setIsLoaded(true); //initial load is done
        }


    }, [sellerId, filter]);

    const loadMessages = useCallback(async (threadId: number) => {
        try {
            const messages = await messageService.getMessages(threadId);
            setMessages(messages);


        }
        catch (error) {
            console.error("Error loading messages:", error);
            setServiceError('Failed to load messages. Please try again later.');
        }

    }, []);

    const loadAttachmentsForMessage = useCallback(
        async (threadId: number, messageId: number) => {
            try {
                const attachments = await messageService.getMessageAttachments(
                    threadId,
                    messageId
                );

                setMessages(prevMessages =>
                    prevMessages.map(msg =>
                        msg.messageId === messageId
                            ? {
                                ...msg,
                                attachments: attachments
                            }
                            : msg
                    )
                );
            } catch (error) {
                console.error(
                    `Error loading attachments for message ${messageId}:`,
                    error
                );
            }
        },
        []
    );



    useEffect(() => {

        if (!isLoaded) {
            setIsLoading(true);
        }
        loadThreads(pageNum);
    }, [pageNum, filter]);

    useEffect(() => {

    }, [filter]);

    useEffect(() => {
        if (selectedThread) {
            loadMessages(selectedThread.messageThreadId);
        }
    }, [selectedThread, loadMessages]);


    // useEffect(() => {
    //     if (selectedThread && (selectedThread.itemId || selectedThread.offerId)) {
    //         setViewReady(true);
    //     }
    // }, [selectedThread]);

    useEffect(() => {
        if (!hasMoreThreads || !bottomRef.current) return;

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    setPageNum(prev => prev + 1);
                }
            },
            { threshold: 1.0 }
        );

        const refCurrent = bottomRef.current;

        // Delay observer to allow layout to settle (helps with large screens)
        const timer = setTimeout(() => {
            if (refCurrent) {
                observer.observe(refCurrent);
            }
        }, 100); // delay 100ms

        return () => {
            clearTimeout(timer);
            if (refCurrent) observer.unobserve(refCurrent);
        };
    }, [bottomRef.current, hasMoreThreads]);

    useEffect(() => {
        const container = document.querySelector('.list-group');
        const shouldLoad = container && container.scrollHeight <= container.clientHeight;

        if (shouldLoad && hasMoreThreads && !isLoadingThreadsRef.current) {
            setPageNum(prev => prev + 1);
        }
    }, [messageThreads, hasMoreThreads]);

    useEffect(() => {
        if (bottomMessageRef.current) {
            bottomMessageRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    return (
        <div className='mt-3 mb-3' style={{ height: 'calc(100vh - 169px)' }}>
            {isLoading && (
                <div className="text-center gap-2 mt-3 text-secondary">
                    <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                    <span>  Loading...</span>
                </div>
            )}
            {!isLoading && (<div className="card h-100 rounded-0 pb-4 ">
                <div className="card-body  h-100  p-4">
                    <h5 className="card-title mb-3">Messages</h5>

                    <div className="d-flex h-100 pb-4" >
                        {!isMobile && (
                            <>
                                <div style={{ width: '300px' }} className="d-flex flex-column">
                                    <div className="btn-group mb-1 me-1" role="group" aria-label="Message filter buttons" style={{ marginLeft: '1px' }}>
                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="btnradio"
                                            id="btnRadioAll"
                                            autoComplete="off"
                                            checked={filter === 'all'}
                                            onChange={() => handleFilterChange('all')}
                                        />
                                        <label className="btn btn-outline-dark btn-sm rounded-0" htmlFor="btnRadioAll" style={{ outline: 'none', boxShadow: 'none' }}>
                                            All
                                        </label>

                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="btnradio"
                                            id="btnRadioRead"
                                            autoComplete="off"
                                            checked={filter === 'read'}
                                            onChange={() => handleFilterChange('read')}
                                        />
                                        <label className="btn btn-outline-dark btn-sm rounded-0" htmlFor="btnRadioRead" style={{ outline: 'none', boxShadow: 'none' }}>
                                            Read
                                        </label>

                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="btnradio"
                                            id="btnRadioUnread"
                                            autoComplete="off"
                                            checked={filter === 'unread'}
                                            onChange={() => handleFilterChange('unread')}
                                        />
                                        <label className="btn btn-outline-dark btn-sm rounded-0" htmlFor="btnRadioUnread" style={{ outline: 'none', boxShadow: 'none' }}>
                                            Unread
                                        </label>
                                    </div>
                                    <ul className="border  mb-0 me-1  list-group rounded-0" style={{ overflowY: 'auto', flex: 1 }}>
                                        {messageThreads.length === 0 ? (
                                            <li className="d-flex flex-column justify-content-center align-items-center text-muted" style={{ height: '150px' }}>
                                                <i className="bi bi-chat-dots fs-2 mb-2"></i>
                                                <span>No messages available.</span>
                                            </li>
                                        ) : (messageThreads.map((thread) => (
                                            <li
                                                key={thread.messageThreadId}
                                                className={`list-group-item border-top-0 border-end-0 border-start-0 border-bottom ${selectedThread?.messageThreadId === thread.messageThreadId ? 'active' : ''} rounded-0`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleThreadClick(thread)}>
                                                <div className={`${thread.sellerUnread ? 'fw-bold' : 'fw-normal'} text-truncate`}>{thread.subject}</div>
                                                {thread.lastMessage && (
                                                    <div className={`text-muted small text-truncate`}>{thread.lastMessage.messageContent}</div>
                                                )}
                                                <div className="text-muted small">{formatDate(thread.lastUpdatedTime)}</div>
                                            </li>
                                        )))}
                                        <li ref={bottomRef} className="list-group-item border-0 text-center text-muted small rounded-0" style={{ cursor: 'pointer' }}>
                                            {hasMoreThreads && (

                                                <div className="d-flex justify-content-center align-items-center gap-2">
                                                    <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                                                    <span>Loading more ...</span>
                                                </div>

                                            )}
                                        </li>
                                    </ul>
                                </div>
                            </>)}
                        {isMobile && (<>
                            {!mobileSidebarOpen && (
                                <button className={`position-absolute btn border btn ms-2 mt-2 `} onClick={() => setMobileSidebarOpen(true)}>
                                    <i className="bi bi-list fs-2"></i>
                                </button>)}

                            {mobileSidebarOpen && (
                                <SidebarComp close={() => setMobileSidebarOpen(false)}>
                                    <MessageThreadListComp
                                        messageThreads={messageThreads}
                                        selectedThread={selectedThread ?? null}
                                        filter={filter}
                                        handleThreadClick={(thread) => { handleThreadClick(thread); setMobileSidebarOpen(false); }}
                                        handleFilterChange={handleFilterChange}
                                    >
                                        <li ref={bottomRef} className="list-group-item border-0 text-center text-muted small rounded-0" style={{ cursor: 'pointer' }}>
                                            {hasMoreThreads && (

                                                <div className="d-flex justify-content-center align-items-center gap-2">
                                                    <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                                                    <span>Loading more ...</span>
                                                </div>

                                            )}
                                        </li>
                                    </MessageThreadListComp>
                                </SidebarComp>
                            )}
                        </>
                        )}
                        {((isMobile && !mobileSidebarOpen) || (!isMobile)) && (<>
                            <div className="flex-grow-1  d-flex flex-column border">
                                <div className={`p-4 rounded-0 ${isMobile ? 'ms-5' : ''}`}>
                                    <div
                                        className={`${isMobile ? 'small fw-semibold' : 'h5'} mb-0`}
                                        style={{
                                            lineHeight: isMobile ? '1.2' : undefined,
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {selectedThread
                                            ? `${selectedThreadBuyerName}  •  ${messageThreads.find((t) => t.messageThreadId === selectedThread.messageThreadId)?.subject
                                            }`
                                            : 'Select a message'}
                                    </div>
                                </div>
                                <div className="p-4 flex-grow-1 overflow-auto  d-flex flex-column border-top" ref={messageContainerRef}>
                                    {selectedThread && selectedThread.itemId && isItemInfoLoaded && !selectedItemInfo && !selectedThread.offerId && (
                                        <div className='text-danger small mb-3'>
                                            <i className="bi bi-info-circle-fill"></i> Item in this message is no longer available.
                                        </div>
                                    )}
                                    {selectedThread && selectedItemInfo && (
                                        <>
                                            <div className="text-muted small">Item mentioned in message
                                            </div>
                                            <div className='d-flex align-items-center border mb-4'>
                                                <img
                                                    src={selectedItemInfo.primaryImage ? `${itemImageURL}/${selectedItemInfo.primaryImage}` : "/images/fallback.png"}
                                                    alt={selectedItemInfo.itemName}
                                                    className="img-fluid rounded-start border-0 border-end"
                                                    style={{ height: '100px', width: '100px', objectFit: 'contain' }}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.onerror = null;
                                                        target.src = "/images/fallback.png";

                                                    }}

                                                />
                                                <div className='d-flex flex-column g-3 ms-3 flex-grow-1'>
                                                    <div className="fw-bold small mt-2 text-truncate one-line-text-ellipsis">
                                                        {selectedItemInfo.itemName}
                                                    </div>
                                                    <div className='text-muted small'>{formatPrice(selectedItemInfo.price)}</div>

                                                    <div className="text-muted small mb-2 text-truncate one-line-text-ellipsis">
                                                        {selectedItemInfo.description}
                                                    </div>

                                                </div>
                                                <div className="me-2">
                                                    <Link
                                                        to={`/items/view/${selectedItemInfo.itemId}`}
                                                        state={{ categories }}
                                                        className="btn btn-sm btn-outline-dark rounded-0"
                                                        style={{ minWidth: '80px' }}
                                                    >
                                                        View
                                                    </Link>

                                                </div>
                                            </div>
                                        </>

                                    )}

                                    {selectedThread && selectedOfferInfo && (
                                        <>
                                            <div className="text-muted small">Offer mentioned in message
                                            </div>
                                            <div className='d-flex align-items-center border mb-4'>
                                                <img
                                                    src={selectedOfferInfo.itemImage ? `${itemImageURL}/${selectedOfferInfo.itemImage}` : "/images/fallback.png"}
                                                    alt={selectedOfferInfo.itemName}
                                                    className="img-fluid rounded-start border-0 border-end"
                                                    style={{ height: '100px', width: '100px', objectFit: 'contain' }}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.onerror = null;
                                                        target.src = "/images/fallback.png";

                                                    }}

                                                />
                                                <div className='d-flex flex-column g-3 ms-3 flex-grow-1'>
                                                    <div className="fw-bold small text-truncate one-line-text-ellipsis">
                                                        {selectedOfferInfo.itemName}
                                                    </div>
                                                    <div className='text-muted small'>Price: {formatPrice(selectedOfferInfo.itemPrice)}</div>
                                                    <div className='text-muted small'>Qty: {selectedOfferInfo.quantity}</div>
                                                    <div className='text-muted small'>Amount: {formatPrice(selectedOfferInfo.amount)}</div>


                                                </div>
                                                <div className="me-2">
                                                    {selectedOfferInfo && (
                                                        <Link
                                                            to={`/offers/view/${selectedOfferInfo.offerId}`}
                                                            state={{ offer: selectedOfferInfo }}
                                                            className="btn btn-sm btn-outline-dark rounded-0"
                                                            style={{ minWidth: '80px' }}
                                                        >
                                                            View
                                                        </Link>
                                                    )}

                                                </div>
                                            </div>
                                        </>

                                    )}

                                    {messages.length > 0 ? (
                                        messages.map((msg) => (
                                            <MessageBubbleComp
                                                key={msg.messageId}
                                                msg={msg}
                                                threadId={selectedThread!.messageThreadId}
                                                onOpenAttachment={handleOpenAttachment}
                                            />
                                        ))
                                    ) : (
                                        <div className="text-muted"></div>
                                    )}
                                    <div ref={bottomMessageRef} />
                                    {serviceError && <div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceError}</div></div>}

                                </div>

                                <div
                                    className="border-top p-3"
                                    style={{
                                        minHeight: 150,
                                        height: "auto",
                                        flexShrink: 0
                                    }}
                                >
                                    <div className="d-flex flex-column h-100">

                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            className="d-none"
                                            accept={acceptedFileTypes}
                                            onChange={handleFilesSelected}
                                        />


                                        {attachmentError && (
                                            <div className="text-danger small">
                                                {attachmentError}
                                            </div>
                                        )}


                                        {attachments.length > 0 && (
                                            <div className="mb-2 d-flex flex-wrap gap-2 align-items-start">
                                                {attachments.map((f, idx) => (
                                                    <span
                                                        key={`${f.name}-${f.size}-${idx}`}
                                                        title={f.name}
                                                        className="badge bg-secondary d-inline-flex align-items-center"
                                                        style={{
                                                            maxWidth: isMobile ? "79px" : "350px",
                                                            paddingRight: "6px"
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                minWidth: 0,
                                                                flex: 1
                                                            }}

                                                        >
                                                            {f.name}
                                                        </span>

                                                        <button
                                                            type="button"
                                                            className="btn-close btn-close-white ms-2"
                                                            aria-label="Remove attachment"
                                                            onClick={() => handleRemoveAttachment(idx)}
                                                            style={{
                                                                fontSize: "0.6rem",
                                                                flexShrink: 0
                                                            }}
                                                            disabled={isSending}
                                                        />
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <textarea
                                            className="form-control flex-grow-1 my-2 rounded-0"
                                            placeholder="Type your message..."
                                            style={{ resize: "none", minHeight: isMobile ? 80 : 70 }}
                                            value={message}
                                            ref={messageInputRef}
                                            onChange={(e) => setMessage(e.target.value)}
                                            disabled={isSending || !selectedThread}
                                        />
                                        <div className={`d-flex ${isMobile ? 'flex-column' : 'justify-content-between'} gap-2`}>
                                            <div className="form-check me-auto">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="sendEmailCopyCheckbox"
                                                    checked={sendEmailCopy}
                                                    disabled={!selectedThread || isSending || isUploadingAttachments}
                                                    onChange={(e) => setSendEmailCopy(e.target.checked)}
                                                />
                                                <label
                                                    className="form-check-label small text-muted"
                                                    htmlFor="sendEmailCopyCheckbox"
                                                >
                                                    Send buyer an email copy
                                                </label>
                                            </div>
                                            <div className={`d-flex gap-2 ${isMobile ? 'w-100' : ''}`}>
                                                <button
                                                    className={`btn btn-outline-dark rounded-0 d-flex align-items-center justify-content-center ${isMobile ? 'flex-grow-0 float-end' : ''}`}
                                                    type="button"
                                                    onClick={handleAddAttachmentsClick}
                                                    disabled={
                                                        !selectedThread ||
                                                        isSending ||
                                                        !message.trim() ||
                                                        attachments.length >= 3 ||
                                                        isUploadingAttachments
                                                    }
                                                >
                                                    <i className="bi bi-paperclip me-1"></i>
                                                    {isMobile ? '' : 'Add Attachments'}
                                                    {isMobile && <span className="ms-1">Attach</span>}
                                                </button>
                                                <button
                                                    className={`btn btn-dark rounded-0 d-flex align-items-center justify-content-center ${isMobile ? 'flex-grow-0 float-end' : ''}`}
                                                    type="button"
                                                    disabled={!message.trim() || !selectedThread || isSending || isUploadingAttachments}
                                                    onClick={handleSend}
                                                >
                                                    {isSending ? (
                                                        <>
                                                            <span
                                                                className="spinner-border spinner-border-sm me-2"
                                                                role="status"
                                                                aria-hidden="true"
                                                            ></span>
                                                            Sending...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-send me-1"></i> Send
                                                        </>
                                                    )}
                                                </button>
                                            </div>

                                        </div>

                                    </div>
                                </div>
                            </div>
                        </>)}



                    </div>
                </div>
            </div>)}
            <UploadProgressModalComp
                show={isUploadingAttachments}
                title={uploadStatusText || 'Uploading attachments'}
                progress={uploadProgress}
                errorMessage=""
                onClose={handleUploadModalClose}
            />
        </div>


    );
};

export default MessagesPage;